import * as rq from '@tanstack/react-query';

import { appConfig } from '@/config';
import { type Device, DeviceSchema } from '@/entities/device';
import { useAuth } from '@/hooks/use-auth';
import { useMe } from '@/hooks/use-me';

import { commonV1DeviceDetailsRetrieve } from '../../__generated__/oazapfts-client';
import { deviceMapper } from '../../mappers/entities/device.mapper';

export namespace GetOneDevice {
  export const key = (input: Input) => [
    'devices',
    'get-one-device',
    ...Object.entries(input).flatMap(([key, value]) => [key, value]),
  ];

  export type Params = { refetchInterval?: number };
  export type Input = {
    id: string | undefined;
    includeChildren?: boolean;
  };
  export type Output = Device | null;

  export function useQuery(input: Input, { refetchInterval }: Params = {}) {
    const auth = useAuth();
    const me = useMe();

    return rq.useQuery<Output>({
      refetchInterval,
      queryKey: key(input),
      enabled: Boolean(input.id),
      queryFn: async () => {
        if (!input.id) {
          return null;
        }

        const accessToken = await auth.getAccessToken();
        const result = await commonV1DeviceDetailsRetrieve(
          {
            accureId: input.id,
            xUserName: me.displayName,
            xUserEmail: me.email,
          },
          {
            baseUrl: `${appConfig.bffUrl}/plex`,
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (result.status === 200) {
          const device = result.data;
          if (!device) {
            return null;
          }

          const parsed = DeviceSchema.safeParse(deviceMapper(device));

          return parsed.success ? parsed.data : null;
        }

        return null;
      },
    });
  }
}
